import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';

const Dashboard = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResults, setSelectedResults] = useState([]);
  const [deletingResults, setDeletingResults] = useState(false);

  // Fetch backtest results
  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await apiService.backtest.getResults(20, 0);
        
        // Safely handle response data - ensure it's an array
        let backTestResults = [];
        if (response && response.data) {
          // Handle both array and non-array responses
          if (Array.isArray(response.data)) {
            backTestResults = response.data;
          } else if (typeof response.data === 'object') {
            // If it's a single object with results, try to extract the array
            if (response.data.results && Array.isArray(response.data.results)) {
              backTestResults = response.data.results;
            } else {
              // If all else fails, try to convert the object to an array if it has keys
              const keys = Object.keys(response.data);
              if (keys.length > 0 && typeof response.data[keys[0]] === 'object') {
                backTestResults = Object.values(response.data);
              } else {
                console.warn('Response data is not in the expected format:', response.data);
                backTestResults = [];
              }
            }
          }
        }
        
        // Further process to ensure each item has an id and other required properties
        const processedResults = backTestResults.map(result => {
          // Ensure every result has an id
          if (!result.id && result._id) result.id = result._id;
          if (!result.id) result.id = Math.random().toString(36).substr(2, 9);
          
          // Handle potential infinity/NaN values in metrics
          if (result.metrics) {
            Object.keys(result.metrics).forEach(key => {
              const value = result.metrics[key];
              if (value === "Infinity" || value === "NaN" || value === "-Infinity") {
                result.metrics[key] = value; // Keep as string for display
              }
            });
          }
          
          return result;
        });
        
        setResults(processedResults);
        setError(null);
      } catch (err) {
        console.error('Error fetching backtest results:', err);
        setError('Failed to load backtest results. Please try again later.');
        setResults([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  // Handle selecting a result
  const handleSelectResult = (resultId) => {
    if (selectedResults.includes(resultId)) {
      setSelectedResults(selectedResults.filter(id => id !== resultId));
    } else {
      setSelectedResults([...selectedResults, resultId]);
    }
  };

  // Handle selecting all results
  const handleSelectAll = () => {
    if (selectedResults.length === results.length) {
      setSelectedResults([]);
    } else {
      setSelectedResults(results.map(result => result.id));
    }
  };

  // Handle deleting selected results
  const handleDeleteSelected = async () => {
    if (selectedResults.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedResults.length} selected result(s)?`)) {
      return;
    }

    setDeletingResults(true);

    try {
      // Delete each selected result
      for (const resultId of selectedResults) {
        await apiService.backtest.deleteResult(resultId);
      }

      // Update results list
      setResults(results.filter(result => !selectedResults.includes(result.id)));
      setSelectedResults([]);
    } catch (err) {
      console.error('Error deleting backtest results:', err);
      setError('Failed to delete selected results. Please try again later.');
    } finally {
      setDeletingResults(false);
    }
  };

  // Format date string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Dashboard
          </h3>
          <Link
            to="/backtest"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            New Backtest
          </Link>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          View and manage your previous backtest runs.
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 m-4 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="px-4 py-5 sm:p-6 text-center">
          <p className="text-gray-500">Loading results...</p>
        </div>
      ) : results.length > 0 ? (
        <div>
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              type="button"
              className="mr-2 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={handleSelectAll}
            >
              {selectedResults.length === results.length ? 'Deselect All' : 'Select All'}
            </button>
            {selectedResults.length > 0 && (
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={handleDeleteSelected}
                disabled={deletingResults}
              >
                {deletingResults ? 'Deleting...' : `Delete Selected (${selectedResults.length})`}
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">Select</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Strategy
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Symbol
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Range
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((result) => (
                  <tr key={result.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                        checked={selectedResults.includes(result.id)}
                        onChange={() => handleSelectResult(result.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{result.strategy_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{result.symbol.toUpperCase()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {result.start_date} to {result.end_date}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(result.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {result.metrics && typeof result.metrics.total_profit !== 'undefined' ? (
                        <>
                          <div className={`text-sm font-medium ${result.metrics.total_profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {result.metrics.total_profit > 0 ? '+' : ''}
                            {result.metrics.total_profit.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Win Rate: {(result.metrics.win_rate * 100).toFixed(2)}%
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-gray-500">No metrics available</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/backtest?result=${result.id}`}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="px-4 py-5 sm:p-6 text-center">
          <p className="text-gray-500">No backtest results found.</p>
          <p className="mt-2 text-sm text-gray-500">
            Run your first backtest to see results here.
          </p>
          <div className="mt-5">
            <Link
              to="/backtest"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Start Backtesting
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
